import { useCallback, useEffect, useReducer, useRef } from 'react';

import { writeDetailsPatch } from '../state';
import type { DetailsPatch, DetailsValues } from '../state';

import { cardEditorReducer, INITIAL_CARD_EDITOR, isDirty } from './cardEditor';
import type { CardId } from './cardEditor';

/** How long "✓ Saved" stays in the card's header (brief §3). */
export const SAVED_MS = 2000;

/**
 * The page's edit mode: the reducer, saving to the store, the two-second
 * "✓ Saved", and the keyboard — Esc cancels, Ctrl/Cmd+Enter saves (§3).
 *
 * The keys listen on the document, as the reference does, so they work
 * wherever focus is — also on the page itself, where a date pick leaves it,
 * out of the modal's reach. A MUI menu or dialog on top stops its own Esc from
 * bubbling, and a field that uses Esc itself (the date panel closing) marks
 * it handled, so neither also cancels the card.
 */
export const useCardEditor = (
  projectId: string,
  values: DetailsValues,
  /** After every save: the card, and the values before and after it (What's changed). */
  onSaved?: (card: CardId, before: DetailsValues, after: DetailsValues) => void,
) => {
  const [state, dispatch] = useReducer(cardEditorReducer, INITIAL_CARD_EDITOR);
  const savedTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const markSaved = useCallback(() => {
    dispatch({ type: 'saved' });
    clearTimeout(savedTimer.current);
    savedTimer.current = setTimeout(() => dispatch({ type: 'savedExpired' }), SAVED_MS);
  }, []);
  useEffect(() => () => clearTimeout(savedTimer.current), []);

  const save = useCallback(() => {
    if (state.editing === null) return;
    writeDetailsPatch(projectId, state.draft);
    onSaved?.(state.editing, values, { ...values, ...state.draft });
    markSaved();
  }, [projectId, state.editing, state.draft, values, onSaved, markSaved]);

  useEffect(() => {
    if (state.editing === null || state.confirmClose) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (event.defaultPrevented) return;
        event.preventDefault();
        dispatch({ type: 'cancel' });
      } else if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        save();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [state.editing, state.confirmClose, save]);

  return {
    state,
    dirty: isDirty(state, values),
    request: (card: CardId) => dispatch({ type: 'request', card, values }),
    change: (patch: DetailsPatch) => dispatch({ type: 'change', patch }),
    cancel: () => dispatch({ type: 'cancel' }),
    save,
    keepEditing: () => dispatch({ type: 'keepEditing' }),
    requestClose: () => dispatch({ type: 'requestClose', values }),
  };
};
